import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { AuthService } from '../auth/auth.service';

/** Métodos de pago que el responsable del evento puede mostrar u ocultar al cliente. */
export const CLIENT_PAYMENT_METHODS: { key: string; label: string; hint: string }[] = [
    { key: 'cash', label: 'Efectivo', hint: 'Ventas cobradas en efectivo en cada barra' },
    { key: 'card', label: 'Tarjeta', hint: 'Cobros con tarjeta desde la app' },
    { key: 'pos', label: 'POS', hint: 'Cobros con datáfono en las barras' },
];

export const ALL_CLIENT_PAYMENT_METHODS = CLIENT_PAYMENT_METHODS.map(m => m.key);

/**
 * Acciones administrativas sobre un evento que van más allá de la operación
 * diaria: vaciar sus órdenes y decidir qué métodos de pago ve el cliente.
 */
@Injectable({
    providedIn: 'root'
})
export class EventAdminService {

    /** Tamaño de lote bajo el límite de 500 escrituras por batch de Firestore. */
    private readonly BATCH_SIZE = 400;

    constructor(
        private api: ApiService,
        private auth: AuthService,
    ) { }

    // ═══════════════════════════════════════════════════════════════════════
    //  MÉTODOS DE PAGO VISIBLES PARA EL CLIENTE
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Lee la lista guardada en el evento. Sin configurar (o vacía) se muestran
     * todos: ocultar tiene que ser una decisión explícita.
     */
    clientPaymentMethods(event: any): string[] {
        const saved = event && Array.isArray(event.client_payment_methods) ? event.client_payment_methods : null;
        const valid = saved ? saved.filter(key => ALL_CLIENT_PAYMENT_METHODS.indexOf(key) >= 0) : [];
        return valid.length > 0 ? valid : ALL_CLIENT_PAYMENT_METHODS.slice();
    }

    async setClientPaymentMethods(eventKey: string, methods: string[]): Promise<any> {
        const clean = ALL_CLIENT_PAYMENT_METHODS.filter(key => (methods || []).indexOf(key) >= 0);
        return this.api.updateDocument('events', eventKey, {
            client_payment_methods: clean,
            client_payment_methods_updated_at: new Date(),
            client_payment_methods_updated_by: this.auth.email || '',
        });
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  INVENTARIO PARA EL CLIENTE (rol 12)
    // ═══════════════════════════════════════════════════════════════════════

    /** Documento de configuración del país (SV vive en 'general'). */
    private get configPath(): string {
        return this.auth.countryCode === 'SV' ? 'general' : this.auth.countryCode;
    }

    private normalizeEmail(email: string): string {
        return String(email || '').trim().toLowerCase();
    }

    /** Correos guardados en el evento con permiso de ver el inventario por zona. */
    inventoryViewers(event: any): string[] {
        const saved = event && Array.isArray(event.inventory_viewers) ? event.inventory_viewers : [];
        return saved.map(e => this.normalizeEmail(e)).filter(e => !!e);
    }

    canViewInventory(event: any, email: string): boolean {
        const mine = this.normalizeEmail(email);
        return !!mine && this.inventoryViewers(event).indexOf(mine) >= 0;
    }

    async setInventoryViewers(eventKey: string, emails: string[]): Promise<any> {
        const clean = Array.from(new Set((emails || []).map(e => this.normalizeEmail(e)).filter(e => !!e)));
        return this.api.updateDocument('events', eventKey, {
            inventory_viewers: clean,
            inventory_viewers_updated_at: new Date(),
            inventory_viewers_updated_by: this.auth.email || '',
        });
    }

    /**
     * Eventos asignados al correo de la sesión en la configuración del país
     * (configurations.users[].events_keys): lo que ven el menú, el monitor,
     * los informes y la cocina para saber "de qué evento" trabaja el usuario.
     */
    async getMyEvents(): Promise<{ key: string; name: string }[]> {
        const email = this.normalizeEmail(this.auth.email || '');
        if (!email) return [];
        const config: any = await this.api.getDocument('configurations', this.configPath);
        const users: any[] = config && Array.isArray(config.users) ? config.users : [];
        const me = users.find(u => u && u.email && this.normalizeEmail(u.email) === email);
        const keys: any[] = me && Array.isArray(me.events_keys) ? me.events_keys.filter(e => !!e) : [];
        return keys
            .map(e => ({ key: String(e.key || e.$key || e.event_key || e.id || ''), name: String(e.name || '').trim() || 'Evento' }))
            .filter(e => !!e.key);
    }

    /**
     * Usuarios con acceso a este evento (configurations.users[].events_keys)
     * y su rol según users-stores. Son los candidatos a ver el inventario:
     * quien no está asignado al evento no puede abrir su resumen de todos modos.
     */
    async getEventUsers(eventKey: string): Promise<{ email: string; role: string }[]> {
        if (!eventKey) return [];

        const config: any = await this.api.getDocument('configurations', this.configPath);
        const users: any[] = config && Array.isArray(config.users) ? config.users : [];
        const assignedUsers = users
            .filter(u => u && u.email && Array.isArray(u.events_keys) && u.events_keys.some(e =>
                e && (e.key === eventKey || e.$key === eventKey || e.id === eventKey || e.event_key === eventKey)));
        const emails = Array.from(new Set(assignedUsers.map(u => this.normalizeEmail(u.email))));

        // Solo las cuentas asignadas al evento, de 10 en 10 (límite del `in`),
        // en vez de todos los users-stores del país por cada apertura. Se
        // busca el correo tal como está escrito y en minúsculas, porque el
        // `in` compara exacto.
        const variantList: string[] = [];
        assignedUsers.forEach(u => {
            const raw = String(u.email).trim();
            variantList.push(raw, raw.toLowerCase());
        });
        const variants: string[] = Array.from(new Set(variantList));
        const roles: { [email: string]: string } = {};
        try {
            const chunks: string[][] = [];
            for (let i = 0; i < variants.length; i += 10) chunks.push(variants.slice(i, i + 10));
            await Promise.all(chunks.map(async chunk => {
                const snap = await this.api.getRef('users-stores').ref
                    .where('email', 'in', chunk)
                    .get();
                snap.forEach(doc => {
                    const data = doc.data();
                    if (!data || !data.email || data.role === undefined || data.role === null) return;
                    roles[this.normalizeEmail(data.email)] = String(data.role);
                });
            }));
        } catch (e) {
            console.warn('No se pudieron leer los roles de users-stores:', e);
        }

        return emails.map(email => ({ email, role: roles[email] || '' }));
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  ELIMINAR LAS ÓRDENES DEL EVENTO
    // ═══════════════════════════════════════════════════════════════════════

    /** Órdenes del evento que todavía no fueron eliminadas. */
    async getDeletableOrders(eventKey: string): Promise<any[]> {
        if (!eventKey) return [];
        const snap = await this.api.getRef('orders').ref
            .where('countryCode', '==', this.auth.countryCode)
            .where('event_key', '==', eventKey)
            .get();
        const list: any[] = [];
        snap.forEach(doc => {
            const order = doc.data();
            if (order.deleted === true) return;
            order.$key = doc.id;
            list.push(order);
        });
        return list;
    }

    /**
     * Eliminación lógica: las reglas de Firestore no permiten borrar órdenes,
     * así que cada una queda cancelada y marcada como eliminada, con quién y
     * cuándo. Las pantallas del evento las dejan de mostrar; el inventario y
     * los cierres de caja no se tocan.
     * Devuelve cuántas órdenes se marcaron.
     */
    async deleteEventOrders(eventKey: string, orders: any[]): Promise<number> {
        const now = new Date();
        const by = this.auth.email || '';
        const firestore = this.api.db.firestore;

        for (let i = 0; i < orders.length; i += this.BATCH_SIZE) {
            const batch = firestore.batch();
            orders.slice(i, i + this.BATCH_SIZE).forEach(order => {
                batch.update(firestore.collection('orders').doc(order.$key), {
                    status: 'canceled',
                    deleted: true,
                    deleted_at: now,
                    deleted_by: by,
                    deleted_previous_status: order.status || '',
                    canceledReason: 'Órdenes del evento eliminadas desde la configuración',
                });
            });
            await batch.commit();
        }

        // Constancia en el evento (las subcolecciones de events no tienen reglas).
        await this.api.updateDocument('events', eventKey, {
            orders_deleted_at: now,
            orders_deleted_by: by,
            orders_deleted_count: orders.length,
        });

        return orders.length;
    }
}
